import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Search,
  Crown,
  Users,
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Eye,
  Sparkles,
  Clock,
  Check,
  X,
  Shield,
  LayoutGrid,
  List,
  Plus,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import OrganisationBillingTab from "@/components/admin/OrganisationBillingTab";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  FEATURE_DEFINITIONS,
  FEATURE_CATEGORIES,
  PLAN_INFO,
} from "@/config/plans";
import { cn } from "@/lib/utils";
import { adminApi, adminFeaturesApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";

// Plan badge component
function PlanBadge({ plan }) {
  const styles = {
    free: {
      backgroundColor: "#f3f4f6",
      color: "#374151",
      borderColor: "#d1d5db",
    },
    starter: {
      backgroundColor: "#dbeafe",
      color: "#1e40af",
      borderColor: "#93c5fd",
    },
    professional: {
      backgroundColor: "#f3e8ff",
      color: "#6b21a8",
      borderColor: "#c4b5fd",
    },
    enterprise: {
      backgroundColor: "#fef3c7",
      color: "#92400e",
      borderColor: "#fcd34d",
    },
  };

  const style = styles[plan] || styles.free;

  return (
    <span
      className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold"
      style={style}
    >
      {PLAN_INFO[plan]?.name || plan}
    </span>
  );
}

// Organisation card component for grid view
const OrganizationCard = ({ org, onView }) => {
  const { t } = useTranslation();
  return (
  <Card
    className="group hover:shadow-md transition-shadow cursor-pointer"
    onClick={() => onView(org)}
  >
    {/* Header */}
    <div className="h-16 bg-primary/10 flex items-center justify-center relative">
      <Building2 className="h-8 w-8 text-primary" />

      {/* View button */}
      <div className="absolute top-2 right-2">
        <Button
          variant="secondary"
          size="icon"
          className="h-8 w-8"
          onClick={(e) => {
            e.stopPropagation();
            onView(org);
          }}
        >
          <Eye className="h-4 w-4" />
        </Button>
      </div>

      {/* Plan badge */}
      <div className="absolute bottom-2 left-2">
        <PlanBadge plan={org.plan} />
      </div>
    </div>

    {/* Content */}
    <CardContent className="p-4">
      <div className="mb-2">
        <h3 className="font-medium truncate">{org.name || t("admin.organisations.unnamed")}</h3>
        <p className="text-sm text-muted-foreground truncate">
          {org.owner_email}
        </p>
      </div>

      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
        <div className="flex items-center gap-1">
          <Users className="h-4 w-4" />
          <span>{org.member_count}</span>
        </div>
        {org.activeOverrides > 0 && (
          <Badge
            variant="outline"
            className="bg-amber-50 text-amber-700 border-amber-200"
          >
            {t("admin.organisations.overridesCount", { count: org.activeOverrides })}
          </Badge>
        )}
      </div>

      <div className="pt-3 border-t text-xs text-muted-foreground flex items-center gap-1">
        <Calendar className="h-3 w-3" />
        {new Date(org.created_at).toLocaleDateString("en-AU")}
      </div>
    </CardContent>
  </Card>
  );
};

export const AdminOrganizations = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();
  const [searchParams] = useSearchParams();

  // UI State
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("grid");
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [planFilter, setPlanFilter] = useState(searchParams.get("plan") || "");

  // Selected organisation for detail view
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Feature override dialog
  // Disabling the signer's ability to decline is a compliance decision, not a
  // UI preference, so it needs an explicit acknowledgement rather than the
  // same one-click confirm as the other ~40 feature switches.
  const [declineAcknowledged, setDeclineAcknowledged] = useState(false);

  const [featureDialog, setFeatureDialog] = useState({
    open: false,
    feature: null,
    enabled: false,
  });
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideExpiry, setOverrideExpiry] = useState("");

  // Create organisation dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", slug: "" });
  const [ownerSearch, setOwnerSearch] = useState("");
  const [selectedOwner, setSelectedOwner] = useState(null);

  // Edit organisation form (General tab)
  const emptyEditForm = {
    name: "",
    billingEmail: "",
    website: "",
    industry: "",
    size: "",
  };
  const [editForm, setEditForm] = useState(emptyEditForm);

  // Delete confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Memoized query params
  const queryParams = useMemo(
    () => ({
      page,
      limit: 20,
      search: search || undefined,
      plan: planFilter || undefined,
    }),
    [page, search, planFilter],
  );

  // Query: Fetch organisations list
  const {
    data: organisationsData,
    isLoading: loading,
    isFetching,
    refetch: refetchOrganizations,
  } = useQuery({
    queryKey: ["admin", "organisations", queryParams],
    queryFn: async () => {
      const response = await adminFeaturesApi.listOrganizations(queryParams);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error("Failed to load organisations");
    },
    placeholderData: (previousData) => previousData,
  });

  // Extract data from query
  const organisations = organisationsData?.organisations || [];
  const pagination = organisationsData?.pagination || {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  };

  // Query: Fetch organisation details (only when selectedOrg is set)
  const { data: orgDetails, isLoading: detailsLoading } = useQuery({
    queryKey: ["admin", "organisation", selectedOrg?.id],
    queryFn: async () => {
      const response = await adminFeaturesApi.getOrganization(selectedOrg.id);
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error("Failed to load organisation details");
    },
    enabled: !!selectedOrg?.id && detailsOpen,
  });

  // Mutation: Update organisation plan
  const updatePlanMutation = useMutation({
    mutationFn: async ({ orgId, plan }) => {
      const response = await adminFeaturesApi.updateOrganizationPlan(orgId, {
        plan,
      });
      return response.data;
    },
    onSuccess: (_, { plan }) => {
      toast.success(`Plan updated to ${PLAN_INFO[plan]?.name}`);
      queryClient.invalidateQueries({
        queryKey: ["admin", "organisation", selectedOrg?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "organisations"] });
    },
    onError: () => {
      toast.error(t("toast.failedUpdatePlan"));
    },
  });

  // Mutation: Set feature override
  const setFeatureOverrideMutation = useMutation({
    mutationFn: async ({ orgId, data }) => {
      const response = await adminFeaturesApi.setFeatureOverride(orgId, data);
      return response.data;
    },
    onSuccess: (_, { data }) => {
      toast.success(`Feature ${data.enabled ? "enabled" : "disabled"}`);
      setFeatureDialog({ open: false, feature: null, enabled: false });
      setOverrideReason("");
      setOverrideExpiry("");
      // Cleared here as well as on open, so a future path that opens this
      // dialog without going through the Switch cannot inherit a stale tick.
      setDeclineAcknowledged(false);
      queryClient.invalidateQueries({
        queryKey: ["admin", "organisation", selectedOrg?.id],
      });
    },
    onError: () => {
      toast.error(t("toast.failedSetOverride"));
    },
  });

  // Mutation: Remove feature override
  const removeFeatureOverrideMutation = useMutation({
    mutationFn: async ({ orgId, feature }) => {
      const response = await adminFeaturesApi.removeFeatureOverride(
        orgId,
        feature,
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("toast.overrideRemoved"));
      queryClient.invalidateQueries({
        queryKey: ["admin", "organisation", selectedOrg?.id],
      });
    },
    onError: () => {
      toast.error(t("toast.failedRemoveOverride"));
    },
  });

  // Mutation: Enable all features
  const enableAllFeaturesMutation = useMutation({
    mutationFn: async (orgId) => {
      const response = await adminFeaturesApi.enableAllFeatures(orgId, {
        reason: "All features enabled by admin",
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("toast.allFeaturesEnabled"));
      queryClient.invalidateQueries({
        queryKey: ["admin", "organisation", selectedOrg?.id],
      });
    },
    onError: () => {
      toast.error(t("toast.failedEnableFeatures"));
    },
  });

  // Mutation: Reset all overrides
  const resetOverridesMutation = useMutation({
    mutationFn: async (orgId) => {
      const response = await adminFeaturesApi.resetFeatureOverrides(orgId);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t("toast.allOverridesReset"));
      queryClient.invalidateQueries({
        queryKey: ["admin", "organisation", selectedOrg?.id],
      });
    },
    onError: () => {
      toast.error(t("toast.failedResetOverrides"));
    },
  });

  // Keep the General tab form in sync with the loaded organisation
  useEffect(() => {
    const org = orgDetails?.organisation;
    if (org) {
      setEditForm({
        name: org.name || "",
        billingEmail: org.billing_email || org.billingEmail || "",
        website: org.website || "",
        industry: org.industry || "",
        size: org.size || "",
      });
    }
  }, [orgDetails]);

  // Query: owner candidates for the create dialog
  const { data: ownerCandidates = [] } = useQuery({
    queryKey: ["admin", "users", "owner-search", ownerSearch],
    queryFn: async () => {
      const response = await adminApi.getUsers({
        search: ownerSearch,
        limit: 8,
      });
      return response.data.data?.users || [];
    },
    enabled: createOpen && ownerSearch.trim().length >= 2,
  });

  // Mutation: Create organisation
  const createOrgMutation = useMutation({
    mutationFn: async () =>
      (
        await adminApi.createOrganization({
          name: createForm.name,
          slug: createForm.slug.trim() || undefined,
          ownerId: selectedOwner?.id,
        })
      ).data,
    onSuccess: () => {
      toast.success(t("team.organisationCreated"));
      setCreateOpen(false);
      setCreateForm({ name: "", slug: "" });
      setOwnerSearch("");
      setSelectedOwner(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "organisations"] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          error.response?.data?.error ||
          t("toast.failedCreateOrg"),
      );
    },
  });

  // Mutation: Update organisation details
  const updateOrgMutation = useMutation({
    mutationFn: async () =>
      (await adminApi.updateOrganization(selectedOrg.id, editForm)).data,
    onSuccess: () => {
      toast.success(t("toast.organisationUpdated"));
      queryClient.invalidateQueries({
        queryKey: ["admin", "organisation", selectedOrg?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["admin", "organisations"] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          error.response?.data?.error ||
          t("toast.failedUpdateOrg"),
      );
    },
  });

  // Mutation: Delete organisation
  const deleteOrgMutation = useMutation({
    mutationFn: async () =>
      (await adminApi.deleteOrganization(selectedOrg.id)).data,
    onSuccess: () => {
      toast.success(t("toast.organisationDeleted"));
      setDeleteConfirmOpen(false);
      setDetailsOpen(false);
      setSelectedOrg(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "organisations"] });
    },
    onError: (error) => {
      setDeleteConfirmOpen(false);
      toast.error(
        error.response?.data?.error?.message ||
          error.response?.data?.error ||
          t("toast.failedDeleteOrg"),
      );
    },
  });

  // Combined loading state for mutations
  const actionLoading =
    updatePlanMutation.isPending ||
    setFeatureOverrideMutation.isPending ||
    removeFeatureOverrideMutation.isPending ||
    enableAllFeaturesMutation.isPending ||
    resetOverridesMutation.isPending ||
    createOrgMutation.isPending ||
    updateOrgMutation.isPending ||
    deleteOrgMutation.isPending;

  // Open organisation details
  const openOrgDetails = (org) => {
    setSelectedOrg(org);
    setDetailsOpen(true);
  };

  // Handler: Update plan
  const updatePlan = (newPlan) => {
    if (!selectedOrg) return;
    updatePlanMutation.mutate({ orgId: selectedOrg.id, plan: newPlan });
  };

  // Disabling the signer's ability to decline is the one override that needs a
  // written justification: feature_override_logs is what you would point at if
  // the decision were ever questioned, and "no reason recorded" is a poor
  // answer. Re-enabling, and every other feature, keep the optional reason.
  const requiresJustification =
    featureDialog.feature === "recipientDecline" && !featureDialog.enabled;
  const justificationMissing =
    requiresJustification && overrideReason.trim().length < 10;

  // Handler: Set feature override
  const setFeatureOverride = () => {
    if (!selectedOrg || !featureDialog.feature) return;
    setFeatureOverrideMutation.mutate({
      orgId: selectedOrg.id,
      data: {
        feature: featureDialog.feature,
        enabled: featureDialog.enabled,
        reason: overrideReason.trim() || undefined,
        expiresAt: overrideExpiry || undefined,
      },
    });
  };

  // Handler: Remove feature override
  const removeFeatureOverride = (feature) => {
    if (!selectedOrg) return;
    removeFeatureOverrideMutation.mutate({
      orgId: selectedOrg.id,
      feature,
    });
  };

  // Handler: Enable all features
  const enableAllFeatures = () => {
    if (!selectedOrg) return;
    enableAllFeaturesMutation.mutate(selectedOrg.id);
  };

  // Handler: Reset all overrides
  const resetOverrides = () => {
    if (!selectedOrg) return;
    resetOverridesMutation.mutate(selectedOrg.id);
  };

  // Check if user is the app owner
  if (!currentUser?.isOwner) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {t("errors.accessDenied")}
            </h2>
            <p className="text-muted-foreground">
              {t("admin.organisations.ownerOnly")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Group features by category
  const groupedFeatures = Object.entries(FEATURE_DEFINITIONS).reduce(
    (acc, [key, def]) => {
      const category = def.category || "other";
      if (!acc[category]) acc[category] = [];
      acc[category].push({ key, ...def });
      return acc;
    },
    {},
  );

  const renderOrganisationGridOrList = () => {
    if (loading) {
      return (
        <Card>
          <CardContent className="p-6 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </CardContent>
        </Card>
      );
    }
    if (organisations?.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="font-medium">
              {t("admin.organisations.noOrgsFound")}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("common.tryAdjustingFilters")}
            </p>
          </CardContent>
        </Card>
      );
    }
    if (viewMode === "grid") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {organisations.map((org) => (
            <OrganizationCard key={org.id} org={org} onView={openOrgDetails} />
          ))}
        </div>
      );
    }
    return (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("team.organisationName")}</TableHead>
                <TableHead>{t("billing.plans.plan")}</TableHead>
                <TableHead>{t("team.members")}</TableHead>
                <TableHead>{t("admin.organisations.overrides")}</TableHead>
                <TableHead>{t("common.created")}</TableHead>
                <TableHead className="w-12.5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {organisations.map((org) => (
                <TableRow
                  key={org.id}
                  className="cursor-pointer"
                  onClick={() => openOrgDetails(org)}
                >
                  <TableCell>
                    <div>
                      <p className="font-medium">{org.name || t("admin.organisations.unnamed")}</p>
                      <p className="text-sm text-muted-foreground">
                        {org.owner_email}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <PlanBadge plan={org.plan} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      {org.member_count}
                    </div>
                  </TableCell>
                  <TableCell>
                    {org.activeOverrides > 0 ? (
                      <Badge
                        variant="outline"
                        className="bg-amber-50 text-amber-700 border-amber-200"
                      >
                        {t("admin.organisations.activeCount", { count: org.activeOverrides })}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(org.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        openOrgDetails(org);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderHistoryClass = (log) => {
    if (log?.action === "enabled") {
      return "bg-green-100 text-green-600";
    }
    return log?.action === "disabled"
      ? "bg-red-100 text-red-600"
      : "bg-gray-100 text-gray-600";
  };

  const renderHistoryTitle = (log) => {
    if (log?.action === "enabled") {
      return <Check className="h-3 w-3" />;
    }
    return log?.action === "disabled" ? (
      <X className="h-3 w-3" />
    ) : (
      <Clock className="h-3 w-3" />
    );
  };

  const renderOrganisationDetails = () => {
    if (detailsLoading) {
      return (
        <div className="py-8 space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      );
    }
    if (orgDetails) {
      return (
        <Tabs defaultValue="general" className="mt-4">
          <TabsList>
            <TabsTrigger value="general">{t("common.general")}</TabsTrigger>
            <TabsTrigger value="features">{t("common.features")}</TabsTrigger>
            <TabsTrigger value="plan">{t("common.plan")}</TabsTrigger>
            <TabsTrigger value="history">{t("common.history")}</TabsTrigger>
            <TabsTrigger value="members">{t("team.members")}</TabsTrigger>
            <TabsTrigger value="billing">{t("common.billing")}</TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("admin.organisations.generalTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("team.organisationName")}</Label>
                    <Input
                      value={editForm.name}
                      onChange={(e) =>
                        setEditForm({ ...editForm, name: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("admin.organisations.billingEmail")}</Label>
                    <Input
                      type="email"
                      value={editForm.billingEmail}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          billingEmail: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("admin.organisations.website")}</Label>
                    <Input
                      value={editForm.website}
                      onChange={(e) =>
                        setEditForm({ ...editForm, website: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("admin.organisations.industry")}</Label>
                    <Input
                      value={editForm.industry}
                      onChange={(e) =>
                        setEditForm({ ...editForm, industry: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("admin.organisations.companySize")}</Label>
                    <Select
                      value={editForm.size || "unset"}
                      onValueChange={(v) =>
                        setEditForm({ ...editForm, size: v === "unset" ? "" : v })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("admin.organisations.notSpecified")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">
                          {t("admin.organisations.notSpecified")}
                        </SelectItem>
                        <SelectItem value="1-10">1-10</SelectItem>
                        <SelectItem value="11-50">11-50</SelectItem>
                        <SelectItem value="51-200">51-200</SelectItem>
                        <SelectItem value="201-1000">201-1000</SelectItem>
                        <SelectItem value="1000+">1000+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={() => updateOrgMutation.mutate()}
                    disabled={actionLoading || !editForm.name.trim()}
                  >
                    {updateOrgMutation.isPending
                      ? t("common.saving")
                      : t("admin.organisations.saveChanges")}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-destructive/40">
              <CardHeader className="py-3">
                <CardTitle className="text-sm font-medium text-destructive flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {t("admin.organisations.dangerZone")}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground">
                  {t("admin.organisations.deleteHint")}
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={actionLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("common.delete")}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features" className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {t("admin.organisations.featuresHint")}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetOverrides}
                  disabled={actionLoading}
                >
                  {t("admin.organisations.resetAll")}
                </Button>
                <Button
                  size="sm"
                  onClick={enableAllFeatures}
                  disabled={actionLoading}
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {t("admin.organisations.enableAll")}
                </Button>
              </div>
            </div>

            {Object.entries(groupedFeatures).map(([category, features]) => (
              <Card key={category}>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm font-medium">
                    {FEATURE_CATEGORIES[category]?.name || category}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {features.map(({ key, name, description }) => {
                      const effectiveFeature =
                        orgDetails.effectiveFeatures?.features?.[key];
                      const hasOverride =
                        orgDetails.effectiveFeatures?.overrides?.[key];
                      const isEnabled = effectiveFeature === true;

                      return (
                        <div
                          key={key}
                          className="flex items-center justify-between py-2"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{name}</p>
                              {hasOverride && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] bg-amber-50 text-amber-700"
                                >
                                  {t("admin.organisations.override")}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {description}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {hasOverride && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFeatureOverride(key)}
                                className="h-7 px-2 text-xs"
                              >
                                {t("admin.organisations.reset")}
                              </Button>
                            )}
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={(enabled) => {
                                setDeclineAcknowledged(false);
                                setFeatureDialog({
                                  open: true,
                                  feature: key,
                                  enabled,
                                });
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Plan Tab */}
          <TabsContent value="plan" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("billing.currentPlan")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Crown className="h-8 w-8 text-amber-500" />
                    <div>
                      <p className="text-xl font-bold">
                        {PLAN_INFO[orgDetails?.organisation?.plan]?.name ||
                          "Free"}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {PLAN_INFO[orgDetails.organisation.plan]?.description}
                      </p>
                    </div>
                  </div>
                  <PlanBadge plan={orgDetails.organisation.plan} />
                </div>

                <div className="space-y-2">
                  <Label>{t("billing.changePlan")}</Label>
                  <Select
                    value={orgDetails.organisation.plan || "free"}
                    onValueChange={updatePlan}
                    disabled={actionLoading}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">{t("common.free")}</SelectItem>
                      <SelectItem value="starter">
                        {t("admin.organisations.starterPrice")}
                      </SelectItem>
                      <SelectItem value="professional">
                        {t("admin.organisations.professionalPrice")}
                      </SelectItem>
                      <SelectItem value="enterprise">
                        {t("billing.plans.enterpriseCustom")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t("admin.organisations.planChangeHint")}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("admin.featureOverrideHistory")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {orgDetails.overrideLogs?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    {t("admin.organisations.noOverrideHistory")}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {orgDetails.overrideLogs?.map((log) => (
                      <div
                        key={log.id}
                        className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
                      >
                        <div
                          className={cn(
                            "rounded-full p-1.5",
                            renderHistoryClass(log),
                          )}
                        >
                          {renderHistoryTitle(log)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm">
                            <span className="font-medium">
                              {FEATURE_DEFINITIONS[log.feature]?.name ||
                                log.feature}
                            </span>{" "}
                            {log.action}
                          </p>
                          {log.reason && (
                            <p className="text-xs text-muted-foreground">
                              {log.reason}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {log.admin_email &&
                              `${t("admin.organisations.byAdmin", { email: log.admin_email })} • `}
                            {new Date(log.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t("team.members")}</CardTitle>
              </CardHeader>
              <CardContent>
                {orgDetails.members?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    {t("admin.organisations.noMembers")}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {orgDetails.members?.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div>
                          <p className="font-medium">
                            {member.first_name} {member.last_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {member.email}
                          </p>
                        </div>
                        <Badge variant="outline">{member.role}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <OrganisationBillingTab
              organisationId={orgDetails?.organisation?.id}
              organisationName={orgDetails?.organisation?.name}
            />
          </TabsContent>
        </Tabs>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("common.organisations")}
          </h1>
          <p className="text-muted-foreground">
            {t("admin.organisations.subtitle")}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => refetchOrganizations()}
            variant="outline"
            className="gap-2"
          >
            <RefreshCw
              className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
            />
            {t("common.refresh")}
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            {t("admin.organisations.newOrganisation")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("placeholders.searchOrganizations")}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-10"
              />
            </div>
            <Select
              value={planFilter || "all"}
              onValueChange={(value) => {
                setPlanFilter(value === "all" ? "" : value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder={t("admin.organisations.allPlans")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("admin.organisations.allPlans")}
                </SelectItem>
                <SelectItem value="free">{t("billing.plans.free")}</SelectItem>
                <SelectItem value="starter">
                  {t("billing.plans.starter")}
                </SelectItem>
                <SelectItem value="professional">
                  {t("billing.plans.professional")}
                </SelectItem>
                <SelectItem value="enterprise">
                  {t("billing.plans.enterprise")}
                </SelectItem>
              </SelectContent>
            </Select>

            {/* View toggle */}
            <div className="flex items-center border rounded-lg p-1">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setViewMode("grid")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Organisations Grid/Table */}
      {renderOrganisationGridOrList()}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t("admin.organisations.showingOf", {
              shown: organisations.length,
              total: pagination.total,
            })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((prev) => prev - 1)}
              disabled={page <= 1 || isFetching}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={page >= pagination.totalPages || isFetching}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Organisation Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Building2 className="h-5 w-5" />
              {selectedOrg?.name || t("admin.organisations.detailsFallbackTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("admin.organisations.detailsSubtitle")}
            </DialogDescription>
          </DialogHeader>

          {renderOrganisationDetails()}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsOpen(false)}>
              {t("common.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feature Override Dialog */}
      <Dialog
        open={featureDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDeclineAcknowledged(false);
            setFeatureDialog({ open: false, feature: null, enabled: false });
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {featureDialog.enabled
                ? t("admin.organisations.enableFeature")
                : t("admin.organisations.disableFeature")}
            </DialogTitle>
            <DialogDescription>
              {FEATURE_DEFINITIONS[featureDialog.feature]?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="override-reason">
                {requiresJustification
                  ? "Reason (required)"
                  : t("common.reasonOptional")}
              </Label>
              <Textarea
                id="override-reason"
                placeholder={
                  requiresJustification
                    ? "Why is this organisation removing the option to decline?"
                    : t("placeholders.overrideReason")
                }
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                aria-invalid={justificationMissing || undefined}
              />
              {justificationMissing && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Recorded against this organisation in the feature override
                  log. Please give at least a short justification.
                </p>
              )}
            </div>

            {featureDialog.feature === "recipientDecline" &&
              !featureDialog.enabled && (
                <div className="rounded-md border border-amber-500/50 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                    This removes the signer&apos;s ability to refuse
                  </p>
                  <p className="text-xs text-amber-900/80 dark:text-amber-200/80">
                    Electronic signature law (ESIGN, eIDAS) treats consent as
                    valid only where it can be refused. An audit trail showing
                    no route to decline is weaker evidence that a signature was
                    given freely. Signers can still abandon the document by
                    closing the window, but no decline will be recorded against
                    the envelope.
                  </p>
                  <div className="flex items-start gap-2 pt-1">
                    <Checkbox
                      id="decline-ack"
                      className="mt-0.5"
                      checked={declineAcknowledged}
                      onCheckedChange={(v) =>
                        setDeclineAcknowledged(v === true)
                      }
                    />
                    <Label
                      htmlFor="decline-ack"
                      className="text-xs font-normal leading-snug cursor-pointer text-amber-900 dark:text-amber-200"
                    >
                      I understand the compliance implication and am applying
                      this as a deliberate policy for this organisation.
                    </Label>
                  </div>
                </div>
              )}

            {featureDialog.enabled && (
              <div className="space-y-2">
                <Label>{t("common.expiryDateOptional")}</Label>
                <Input
                  type="datetime-local"
                  value={overrideExpiry}
                  onChange={(e) => setOverrideExpiry(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t("admin.organisations.noExpiryHint")}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setFeatureDialog({ open: false, feature: null, enabled: false })
              }
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={setFeatureOverride}
              disabled={
                actionLoading ||
                justificationMissing ||
                (requiresJustification && !declineAcknowledged)
              }
            >
              {actionLoading ? t("common.saving") : t("common.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Organisation Dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) {
            setCreateForm({ name: "", slug: "" });
            setOwnerSearch("");
            setSelectedOwner(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {t("admin.organisations.newOrganisation")}
            </DialogTitle>
            <DialogDescription>
              {t("admin.organisations.createSubtitle")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("team.organisationName")}</Label>
              <Input
                value={createForm.name}
                onChange={(e) =>
                  setCreateForm({ ...createForm, name: e.target.value })
                }
                placeholder="Acme Inc"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("admin.organisations.slugOptional")}</Label>
              <Input
                value={createForm.slug}
                onChange={(e) =>
                  setCreateForm({ ...createForm, slug: e.target.value })
                }
                placeholder={t("admin.organisations.slugHint")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("common.owner")}</Label>
              {selectedOwner ? (
                <div className="flex items-center justify-between p-2.5 rounded-lg border bg-muted/50">
                  <div>
                    <p className="text-sm font-medium">
                      {selectedOwner.firstName || selectedOwner.first_name}{" "}
                      {selectedOwner.lastName || selectedOwner.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {selectedOwner.email}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedOwner(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={ownerSearch}
                      onChange={(e) => setOwnerSearch(e.target.value)}
                      placeholder={t("admin.organisations.ownerSearchPlaceholder")}
                      className="pl-10"
                    />
                  </div>
                  {ownerSearch.trim().length >= 2 &&
                    ownerCandidates.length > 0 && (
                      <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                        {ownerCandidates.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            className="w-full text-left p-2.5 hover:bg-muted/50 transition-colors"
                            onClick={() => setSelectedOwner(user)}
                          >
                            <p className="text-sm font-medium">
                              {user.firstName || user.first_name}{" "}
                              {user.lastName || user.last_name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {user.email}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => createOrgMutation.mutate()}
              disabled={
                actionLoading || !createForm.name.trim() || !selectedOwner
              }
            >
              {createOrgMutation.isPending
                ? t("common.creating")
                : t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Organisation Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {t("admin.organisations.deleteTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("admin.organisations.deleteConfirm", {
                name: selectedOrg?.name,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteOrgMutation.mutate()}
              disabled={deleteOrgMutation.isPending}
            >
              {deleteOrgMutation.isPending ? t("common.deleting") : t("common.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrganizations;
